import { Button } from 'components';
import { PartnerForm } from 'components/Forms/PartnerForm';
import { FormMessage } from 'components/Message';
import NavigationPrompt from 'components/NavigationPrompt';
import { DashboardTitle } from 'components/Titles';
import { useUser } from 'hooks/useUser';
import { useDisclose } from 'hooks/utilHooks';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm, useFormState } from 'react-hook-form';
import { partnerUpdateProfileValidation } from 'schemas';
import { PartnerService } from 'services';
import { IPartnerCreate, partnerDefaultValues } from 'types/Partner';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import _ from 'lodash';

export const PartnerProfile: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const { palette } = useTheme();
  const { user, getUser } = useUser();
  const confirmDialog = useDisclose();
  const savedDialog = useDisclose();
  const updateDialog = useDisclose();

  const partner = user as IPartnerCreate;

  /* If user hasn't completed registration */
  const firstAccess = !partner.acceptPartnershipTerms?.isAccepted;

  document.title = 'Vet4all - Parceiro';

  const form = useForm<IPartnerCreate>({
    defaultValues: partnerDefaultValues,
    resolver: partnerUpdateProfileValidation,
    mode: 'onBlur',
  });

  const { dirtyFields } = useFormState({ control: form.control });

  const { address, socialMedias } = dirtyFields;

  const hasChangedAddress = !_.isEmpty(address);
  const hasChangedSocialMedias = !_.isEmpty(socialMedias);

  const hasChangedRequiredFields =
    !firstAccess && (hasChangedAddress || hasChangedSocialMedias);

  const onSubmit = async (values: IPartnerCreate) => {
    try {
      setLoading(true);
      confirmDialog.onClose();

      await PartnerService.partnerUpdateProfile(values);

      setSuccess('Parceiro atualizado com sucesso');
      getUser();

      if (firstAccess || hasChangedRequiredFields) {
        savedDialog.onOpen();
      } else {
        updateDialog.onOpen();
      }
    } catch (reason) {
      const { message } = reason as Error;
      setError(message);
    } finally {
      setLoading(false);

      setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
    }

    return;
  };

  const handleSave = () => {
    if (hasChangedRequiredFields) {
      return confirmDialog.onOpen();
    }

    onSubmit(form.getValues());
  };

  useEffect(() => {
    form.reset({
      ...partnerDefaultValues,
      ...partner,
      discountsUpdate: partner?.discounts,
    });
  }, [form, partner]);

  return (
    <Grid container>
      <NavigationPrompt block={form.formState.isDirty} />

      <FormProvider {...form}>
        <Grid item xs={12}>
          <DashboardTitle title="PERFIL" />
        </Grid>

        <Grid item xs={12}>
          <Paper
            variant="outlined"
            sx={{ border: '2px solid', borderColor: palette.primary.main }}
          >
            {loading && <LinearProgress />}

            <PartnerForm />
          </Paper>

          {!firstAccess && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              *Caso precise editar CNPJ, Razão Social, Nome fantasia ou Nome do
              responsável, entre em contato conosco através do
              atendimento@vet4all.com.br
            </Typography>
          )}

          {error && (
            <Box p={1}>
              <Grid container justifyContent="flex-end">
                <Grid item xs={6}>
                  <FormMessage data={error} severity="error" />
                </Grid>
              </Grid>
            </Box>
          )}

          {success && (
            <Box p={1}>
              <Grid container justifyContent="flex-end">
                <Grid item xs={6}>
                  <FormMessage data={success} severity="success" />
                </Grid>
              </Grid>
            </Box>
          )}

          <Box py={1}>
            <Grid container spacing={1} justifyContent="flex-end">
              <Grid item>
                <Button
                  disabled={loading}
                  onClick={form.handleSubmit(handleSave)}
                  loading={loading}
                >
                  SALVAR
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </FormProvider>

      <Dialog open={savedDialog.isOpen} onClose={savedDialog.onClose}>
        <DialogTitle>
          Confirmação de envio
          <IconButton
            aria-label="close"
            onClick={savedDialog.onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pb: 0 }}>
          <DialogContentText>
            Seu cadastro será analisado, aguarde pelo e-mail de aprovação.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button variant="text" onClick={savedDialog.onClose}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={updateDialog.isOpen} onClose={updateDialog.onClose}>
        <DialogTitle>
          Confirmação de envio
          <IconButton
            aria-label="close"
            onClick={updateDialog.onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pb: 0 }}>
          <DialogContentText>
            A informações foram salvas com sucesso
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button variant="text" onClick={updateDialog.onClose}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialog.isOpen} onClose={confirmDialog.onClose}>
        <DialogTitle>
          Edição dos dados cadastrais
          <IconButton
            aria-label="close"
            onClick={confirmDialog.onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pb: 0 }}>
          <DialogContentText>
            Ao editar endereço, site, instagram, facebook é necessário uma nova
            análise dos dados! E você não ficará mais ativo no aplicativo até
            que seja aprovado. Deseja continuar?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button variant="text" onClick={confirmDialog.onClose}>
            NÂO
          </Button>

          <Button variant="text" onClick={form.handleSubmit(onSubmit)}>
            SIM
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};
